/**
 * The credential form for an api-key integration, built to be filled by
 * someone who was handed values by their IT team — and to give that team
 * what it needs without taking the page over.
 *
 * - An integration with two ways in (Laserfiche Cloud vs self-hosted) shows
 *   a switch and only the chosen way's fields; the other way's half-typed
 *   values are never sent.
 * - Every field carries a small info mark. Hovering (or focusing) it opens a
 *   hover card: what the value is and where an admin finds it. The card is
 *   the guide; the page stays a form.
 * - One "IT setup guide" mark on the form's header holds the whole
 *   walkthrough and the vendor's own docs, again as a hover card; clicking
 *   it opens a dialog with pictures of the vendor's app where we have them.
 * - Saving is enabled only when the chosen way is complete.
 *
 * @module components/apps/integrations/IntegrationSetupForm
 */
import {
  BookOpenIcon,
  ExternalLinkIcon,
  ImageIcon,
  InfoIcon,
  Loader2Icon,
  LockIcon,
  PlugIcon,
} from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";

import { cn } from "~/lib/utils";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../../ui/hover-card";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Toggle, ToggleGroup } from "../../ui/toggle-group";
import {
  credentialFieldsFor,
  credentialsComplete,
  credentialValueKey,
  credentialValuesForMode,
  defaultCredentialMode,
  INTEGRATION_CREDENTIAL_MODES,
  INTEGRATION_SETUP_GUIDES,
  INTEGRATION_SETUP_LINKS,
  INTEGRATION_SETUP_SCREENS,
  type CredentialField,
  type FieldGuide,
  type SetupScreen,
} from "./integrationContent";

/** Hover cards open on a deliberate hover; this is the wait, in ms. */
const HOVER_DELAY_MS = 120;

/**
 * The info mark beside a field's label. A real button, so it is reachable
 * by keyboard (focus opens the card) and announced with the field's name.
 */
function FieldGuideMark({ label, guide }: { readonly label: string; readonly guide: FieldGuide }) {
  return (
    <HoverCard>
      <HoverCardTrigger
        delay={HOVER_DELAY_MS}
        render={
          <button
            type="button"
            aria-label={`Where to find ${label}`}
            data-testid="field-guide"
            className="inline-flex size-5 cursor-help items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        }
      >
        <InfoIcon className="size-3.5" />
      </HoverCardTrigger>
      <HoverCardPopup side="right" align="start" sideOffset={8} className="w-80 px-3.5 py-3">
        <p className="text-xs font-semibold text-foreground">{label}</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{guide.what}</p>
        <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs leading-5 text-foreground marker:text-muted-foreground/70">
          {guide.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </HoverCardPopup>
    </HoverCard>
  );
}

/**
 * A numbered mark: on the address strip, over the picture, and again in
 * the legend beneath, so the eye can tie the three together.
 */
function CalloutMark({
  n,
  className,
  style,
}: {
  readonly n: number;
  readonly className?: string;
  readonly style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      style={style}
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground shadow-sm ring-2 ring-background",
        className,
      )}
    >
      {n}
    </span>
  );
}

/**
 * One vendor screen inside the guide dialog: an address strip of ours when
 * the address is itself a value the form asks for, the picture with its
 * marks, and a legend tying each mark to a field on our form.
 */
function SetupScreenFigure({ screen }: { readonly screen: SetupScreen }) {
  const address = screen.address;
  const offset = address ? 1 : 0;
  return (
    <figure data-testid="setup-screen">
      <div className="mx-auto max-w-sm overflow-hidden rounded-lg border border-border/70 bg-muted/40 shadow-sm">
        {address ? (
          <div
            data-testid="setup-screen-address"
            className="flex items-center gap-2 border-b border-border/60 bg-background/80 px-3 py-2"
          >
            <span className="flex gap-1" aria-hidden="true">
              <span className="size-2 rounded-full bg-border" />
              <span className="size-2 rounded-full bg-border" />
              <span className="size-2 rounded-full bg-border" />
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2 rounded-md bg-muted px-2.5 py-1 font-mono text-[11px] sm:text-xs">
              <LockIcon className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span data-testid="setup-screen-url" className="truncate text-muted-foreground">
                https://
                <mark className="rounded-sm bg-primary/15 px-0.5 font-semibold text-foreground">
                  {address.tenant}
                </mark>
                {address.rest}
              </span>
              <CalloutMark n={1} />
            </span>
          </div>
        ) : null}
        <div className="relative" style={{ aspectRatio: `${screen.width} / ${screen.height}` }}>
          <img
            src={screen.src}
            alt={screen.alt}
            width={screen.width}
            height={screen.height}
            loading="lazy"
            decoding="async"
            className="block size-full object-cover"
          />
          {screen.callouts.map((callout, index) => (
            <CalloutMark
              key={callout.field}
              n={index + 1 + offset}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              // Percentages of the picture, so the marks follow it at any size.
              style={{ left: `${callout.x}%`, top: `${callout.y}%` }}
            />
          ))}
        </div>
      </div>
      <figcaption className="mt-3">
        <p className="text-xs font-semibold text-foreground">{screen.title}</p>
        <ol className="mt-2 space-y-2" data-testid="setup-screen-legend">
          {[...(address ? [{ field: address.field, detail: address.detail }] : []), ...screen.callouts].map(
            (callout, index) => (
              <li key={callout.field} className="flex gap-2.5 text-xs leading-5">
                <CalloutMark n={index + 1} className="mt-0.5" />
                <span>
                  <span className="font-medium text-foreground">{callout.field}</span>
                  <span className="text-muted-foreground"> — {callout.detail}</span>
                </span>
              </li>
            ),
          )}
        </ol>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Captured from{" "}
          <a
            href={screen.source.href}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary hover:underline"
          >
            {screen.source.label}
          </a>{" "}
          on {formatCaptured(screen.source.captured)}. The vendor may have changed it since.
        </p>
      </figcaption>
    </figure>
  );
}

const formatCaptured = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/**
 * The whole walkthrough for the firm's IT team, folded into one mark on
 * the form header: hovering shows the steps and the vendor's own pages;
 * clicking opens a dialog that adds pictures of the vendor's app, when we
 * have them, with each part tied to the field it feeds.
 */
export function SetupGuideMark({
  connectorId,
  integrationName,
}: {
  readonly connectorId: string;
  readonly integrationName: string;
}) {
  const steps = INTEGRATION_SETUP_GUIDES[connectorId] ?? [];
  const links = INTEGRATION_SETUP_LINKS[connectorId] ?? [];
  const screens = INTEGRATION_SETUP_SCREENS[connectorId] ?? [];
  const [open, setOpen] = useState(false);
  // The hover card is controlled so that opening the dialog can close it:
  // left to itself it would stay open (and above the dialog) for as long
  // as the pointer rests on the button that was just clicked.
  const [cardOpen, setCardOpen] = useState(false);
  // Focus lands on the dialog itself, not its first link: a link sits
  // under the picture, and focusing it would open the dialog scrolled past
  // the very thing it is for.
  const popupRef = useRef<HTMLDivElement>(null);
  if (steps.length === 0) return null;
  const hasScreens = screens.length > 0;
  const openScreens = () => {
    setCardOpen(false);
    setOpen(true);
  };
  const stepList = (
    <ol className="list-decimal space-y-1.5 pl-4 text-xs leading-5 text-foreground marker:text-muted-foreground/70">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
  const linkList =
    links.length > 0 ? (
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
          >
            {link.label}
            <ExternalLinkIcon className="size-3" />
          </a>
        ))}
      </div>
    ) : null;
  return (
    <>
      <HoverCard open={cardOpen} onOpenChange={setCardOpen}>
        <HoverCardTrigger
          delay={HOVER_DELAY_MS}
          render={
            <button
              type="button"
              data-testid="setup-guide"
              aria-haspopup={hasScreens ? "dialog" : undefined}
              onClick={hasScreens ? openScreens : undefined}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                hasScreens ? "cursor-pointer" : "cursor-help",
              )}
            />
          }
        >
          <BookOpenIcon className="size-3.5" />
          IT setup guide
        </HoverCardTrigger>
        <HoverCardPopup side="bottom" align="end" sideOffset={8} className="w-[26rem] px-4 py-3.5">
          <p className="text-xs font-semibold text-foreground">
            Setting up {integrationName} for Amoreg
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            For whoever administers {integrationName} at your firm.
          </p>
          <div className="mt-2.5">{stepList}</div>
          {linkList !== null || hasScreens ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-t border-border/60 pt-2.5">
              {linkList}
              {hasScreens ? (
                <button
                  type="button"
                  data-testid="setup-guide-screens"
                  onClick={openScreens}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                >
                  <ImageIcon className="size-3" />
                  See it in {integrationName}
                </button>
              ) : null}
            </div>
          ) : null}
        </HoverCardPopup>
      </HoverCard>
      {hasScreens ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogPopup
            ref={popupRef}
            initialFocus={() => popupRef.current}
            className="max-w-2xl"
            data-testid="setup-screens"
          >
            <DialogHeader>
              <DialogTitle className="pr-8 text-lg">Setting up {integrationName} for Amoreg</DialogTitle>
              <DialogDescription>
                What the {integrationName} admin sees, and which part of it goes into the form here.
              </DialogDescription>
            </DialogHeader>
            <DialogPanel className="space-y-6">
              {screens.map((screen) => (
                <SetupScreenFigure key={screen.src} screen={screen} />
              ))}
              <div>
                <p className="text-xs font-semibold text-foreground">The steps</p>
                <div className="mt-2">{stepList}</div>
                {linkList !== null ? <div className="mt-3">{linkList}</div> : null}
              </div>
            </DialogPanel>
          </DialogPopup>
        </Dialog>
      ) : null}
    </>
  );
}

export function IntegrationSetupForm({
  connectorId,
  values,
  onChange,
  disabled,
  submitLabel,
  onSubmit,
}: {
  readonly connectorId: string;
  readonly values: Record<string, string>;
  readonly onChange: (values: Record<string, string>) => void;
  readonly disabled: boolean;
  readonly submitLabel: string;
  /** Receives only the chosen way's values. */
  readonly onSubmit: (values: Record<string, string>) => void;
}) {
  const formId = useId();
  const modes = INTEGRATION_CREDENTIAL_MODES[connectorId] ?? [];
  const [mode, setMode] = useState<string | null>(() => defaultCredentialMode(connectorId));
  // A form reused for another connector starts in that connector's default
  // way in, not the previous one's.
  useEffect(() => {
    setMode(defaultCredentialMode(connectorId));
  }, [connectorId]);
  const fields = credentialFieldsFor(connectorId, mode);
  const complete = credentialsComplete(connectorId, values, mode);
  const submit = () => {
    if (complete && !disabled) onSubmit(credentialValuesForMode(connectorId, values, mode));
  };

  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      data-testid="integration-setup-form"
      data-mode={mode ?? undefined}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {modes.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
          <ToggleGroup
            variant="outline"
            size="sm"
            aria-label="How is it hosted?"
            value={mode === null ? [] : [mode]}
            onValueChange={(next: ReadonlyArray<string>) => {
              const chosen = next[0];
              if (chosen !== undefined && chosen !== mode) setMode(chosen);
            }}
          >
            {modes.map((candidate) => (
              <Toggle
                key={candidate.id}
                value={candidate.id}
                aria-label={candidate.label}
                data-testid={`credential-mode-${candidate.id}`}
                className="cursor-pointer px-3"
              >
                {candidate.label}
              </Toggle>
            ))}
          </ToggleGroup>
          <span className="text-xs text-muted-foreground">
            {modes.find((candidate) => candidate.id === mode)?.description}
          </span>
        </div>
      ) : null}
      {fields.map((field) => (
        // The info mark sits beside the label, not inside it: a control
        // inside a <label> would join the field's accessible name and steal
        // the label's click.
        <div
          key={`${field.mode ?? "*"}:${field.name}`}
          className={cn("flex flex-col gap-1.5", field.multiline && "sm:col-span-2")}
          data-testid={`credential-field-${field.name}`}
        >
          <span className="flex items-center gap-1.5">
            <label
              htmlFor={`${formId}-${field.name}`}
              className="text-sm font-medium text-foreground"
            >
              {field.label}
            </label>
            {field.optional ? (
              <span className="text-xs text-muted-foreground">optional</span>
            ) : null}
            {field.guide ? <FieldGuideMark label={field.label} guide={field.guide} /> : null}
          </span>
          {field.multiline ? (
            // A pasted key file: it needs room, and a password field cannot
            // hold newlines. The value never echoes back after saving, which
            // is the protection a masked field would have given.
            <Textarea
              id={`${formId}-${field.name}`}
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
              rows={5}
              className="font-mono text-xs"
              onChange={(event) =>
                onChange({ ...values, [credentialValueKey(field)]: event.currentTarget.value })
              }
              placeholder={field.placeholder}
              value={values[credentialValueKey(field)] ?? ""}
            />
          ) : (
            <Input
              id={`${formId}-${field.name}`}
              autoComplete="off"
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...values, [credentialValueKey(field)]: event.currentTarget.value })
              }
              placeholder={field.placeholder}
              type={field.secret ? "password" : (field.inputType ?? "text")}
              value={values[credentialValueKey(field)] ?? ""}
            />
          )}
          {field.hint ? <span className="text-xs text-muted-foreground">{field.hint}</span> : null}
        </div>
      ))}
      <div className="flex justify-end sm:col-span-2">
        <Button
          type="submit"
          className="cursor-pointer rounded-full"
          disabled={disabled || !complete}
        >
          {disabled ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <PlugIcon className="size-4" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export type { CredentialField };
